import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, X, Copy } from "lucide-react";
import { API, fmtPrice, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const TABS = ["Artworks", "Notecards", "Inquiries", "Orders", "Journal"];
const EMPTY_ART = { title: "", series: "Highlands", year: "2026", medium: "Watercolor on Cotton Paper", dimensions: "", frame: "", status: "Available in Studio", price_display: "", price_cents: 0, image: "", description: "", palette: [], category: "painting", featured: false };
const STATUS_OPTIONS = ["Available in Studio", "Reserved for Exhibition", "In Private Collection"];
const INQ_STATUSES = ["new", "contacted", "booked", "archived"];
const CARD_SECTIONS = [
  { key: "available", title: "On the open floor", note: "in the shop, for sale now" },
  { key: "soon", title: "Back stock", note: "painted, awaiting release" },
  { key: "sold", title: "Sold", note: "gone to good homes" },
  { key: "retired", title: "Retired", note: "off display, kept in the records" },
];
const CARD_MONTHS = { January: 1, February: 2, March: 3, April: 4, May: 5, June: 6, July: 7, August: 8, September: 9, October: 10, November: 11, December: 12 };
const QUARTER_SHELF_OPTIONS = [
  { v: "", label: "Auto — by painted month" },
  { v: "q1", label: "First Quarter (Jan–Mar)" },
  { v: "q2", label: "Second Quarter (Apr–Jun)" },
  { v: "q3", label: "Third Quarter (Jul–Sep)" },
  { v: "q4", label: "Fourth Quarter (Oct–Dec)" },
];
const cardMonthRank = (card) => {
  const m = (card.description || "").match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d\d)/);
  return m ? Number(m[2]) * 100 + CARD_MONTHS[m[1]] : 99999;
};

const input = "w-full bg-paper border border-ink/15 rounded-md px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:border-terracotta/60 transition-colors";

export default function StudioDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("Artworks");
  const [artworks, setArtworks] = useState([]);
  const [cards, setCards] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null); // artwork object or EMPTY_ART
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    API.get("/artworks").then((r) => setArtworks(r.data));
    API.get("/notecards").then((r) => setCards(r.data));
    API.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    API.get("/admin/payments").then((r) => setOrders(r.data)).catch(() => {});
    API.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  };

  useEffect(() => {
    API.get("/auth/me")
      .then((r) => { setUser(r.data); loadAll(); })
      .catch(() => navigate("/studio/login"));
  }, [navigate]);

  const saveArtwork = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing, price_cents: Number(editing.price_cents) || 0,
      palette: typeof editing.palette === "string" ? editing.palette.split(",").map((s) => s.trim()).filter(Boolean) : editing.palette };
    try {
      if (editing.id) await API.put(`/admin/artworks/${editing.id}`, payload);
      else await API.post("/admin/artworks", payload);
      toast.success(editing.id ? "Artwork updated" : "Artwork added to the wall");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const refundOrder = async (sessionId) => {
    if (!window.confirm("Refund this order in full? Stripe returns the money to the buyer's card.")) return;
    try {
      await API.post(`/admin/payments/${sessionId}/refund`);
      toast.success("Refunded — the money is on its way back. If the card is returning to the shelf, set it to 'available' in the Notecards tab.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeArtwork = async (id) => {
    await API.delete(`/admin/artworks/${id}`);    toast.success("Artwork removed");
    loadAll();
  };

  const setInquiryStatus = async (id, status) => {
    await API.patch(`/admin/inquiries/${id}`, { status });
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
  };

  const logout = async () => {
    await API.post("/auth/logout");
    navigate("/studio/login");
  };

  if (!user) return <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.25em] text-stone2">Checking the studio key…</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-14" data-testid="studio-dashboard">
      <Seo title="Studio Dashboard" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sage">Signed in as {user.email}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2">The Back Room</h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink/20 rounded-full px-5 py-2.5 hover:bg-ink hover:text-linen transition-colors">
          <LogOut size={13} /> Lock the studio
        </button>
      </div>

      <div className="flex gap-2 mt-10 border-b border-ink/10 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(t)}
            className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3 border-b-2 -mb-px transition-colors ${tab === t ? "border-terracotta text-ink" : "border-transparent text-stone2 hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Artworks" && (
        <div className="mt-8">
          <button data-testid="admin-artwork-create-btn" onClick={() => setEditing({ ...EMPTY_ART })}
            className="inline-flex items-center gap-2 bg-ink text-linen rounded-full px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors">
            <Plus size={14} /> Hang a new work
          </button>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {artworks.map((a) => (
              <div key={a.id} data-testid={`admin-artwork-${a.id}`} className="bg-paper border border-ink/10 deckle overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full aspect-[4/3] object-cover" />
                <div className="p-5">
                  <p className="font-serif text-xl text-ink leading-tight">{a.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 mt-1">{a.status} · {a.price_display || "—"}</p>
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`admin-artwork-edit-${a.id}`} onClick={() => setEditing({ ...a })}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] border border-ink/20 rounded-full px-4 py-2 hover:bg-ink hover:text-linen transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                    <button data-testid={`admin-artwork-delete-${a.id}`} onClick={() => removeArtwork(a.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] border border-ink/20 rounded-full px-4 py-2 hover:bg-destructive hover:text-linen hover:border-destructive transition-colors">
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Notecards" && (
        <div className="mt-8 space-y-10">
          {CARD_SECTIONS.map((sec) => {
            const list = cards.filter((c) => (c.status || "available") === sec.key).sort((a, b) => cardMonthRank(a) - cardMonthRank(b));
            return (
              <section key={sec.key} data-testid={`admin-notecard-section-${sec.key}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4 border-b border-ink/10 pb-2">
                  <h3 className="font-serif text-xl text-ink">{sec.title}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">{list.length} · {sec.note}</p>
                </div>
                {list.length === 0 && <p className="font-serif text-stone2 italic">Nothing on this shelf right now.</p>}
                <div className="grid sm:grid-cols-2 gap-6">
                  {list.map((c) => (
            <div key={c.id} data-testid={`admin-notecard-${c.id}`} className="flex gap-5 bg-paper border border-ink/10 deckle p-5">
              <img src={c.image} alt={c.title} className={`w-24 h-24 object-cover rounded-sm border border-ink/10 ${(c.status === "sold" || c.status === "retired") ? "opacity-60 grayscale-[35%]" : ""}`} />
              <div className="flex-1">
                <p className="font-serif text-lg text-ink leading-tight">{c.title}</p>
                <p className="font-mono text-xs text-stone2 mt-1">{fmtPrice(c.price_cents)} · original · one of one</p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                    (c.status === "sold" || c.status === "retired") ? "border-stone2/40 text-stone2" : c.status === "soon" ? "border-ochre/40 text-ochre" : "border-sage/40 text-sage"
                  }`} data-testid={`admin-notecard-status-${c.id}`}>
                    {c.status === "sold" ? "Sold" : c.status === "soon" ? "Coming soon" : c.status === "retired" ? "Retired" : "Available"}
                  </span>
                  {["available", "soon", "sold", "retired"].filter((s) => s !== (c.status || "available")).map((s) => (
                    <button key={s} data-testid={`admin-notecard-set-${s}-${c.id}`}
                      onClick={async () => {
                        try {
                          await API.patch(`/admin/notecards/${c.id}/status`, { status: s });
                          toast.success(`"${c.title}" is now ${s === "soon" ? "coming soon" : s}`);
                          loadAll();
                        } catch {
                          toast.error("That change didn't take — your sign-in may have expired. Lock the studio and sign back in, then try again.");
                        }
                      }}
                      className="font-mono text-[9px] uppercase tracking-[0.15em] text-stone2 hover:text-terracotta underline underline-offset-2 transition-colors">
                      mark {s === "soon" ? "coming soon" : s}
                    </button>
                  ))}
                </div>
                <select value={c.quarter || ""} data-testid={`admin-notecard-quarter-${c.id}`}
                  onChange={async (e) => {
                    try {
                      await API.patch(`/admin/notecards/${c.id}/quarter`, { quarter: e.target.value || null });
                      toast.success(`"${c.title}" refiled on the card table`);
                      loadAll();
                    } catch {
                      toast.error("That change didn't take — your sign-in may have expired. Lock the studio and sign back in, then try again.");
                    }
                  }}
                  aria-label="Card table section"
                  className="mt-2.5 bg-paper border border-ink/15 rounded-full px-3 py-1.5 text-[9px] font-mono uppercase tracking-[0.12em] text-stone2 focus:outline-none focus:border-terracotta/60 transition-colors">
                  {QUARTER_SHELF_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
            </div>
                  ))}
                </div>
              </section>
            );
          })}
          <p className="text-xs text-stone2">Prices are synced with Stripe — ask me to change prices or add new cards.</p>
        </div>
      )}

      {tab === "Inquiries" && (
        <div className="mt-8 space-y-4" data-testid="admin-inquiries-list">
          {inquiries.length === 0 && <p className="font-serif text-2xl text-stone2">The inbox is quiet — no inquiries yet.</p>}
          {inquiries.map((i) => (
            <div key={i.id} className="bg-paper border border-ink/10 deckle p-5 sm:p-6" data-testid={`admin-inquiry-${i.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">{i.topic}{i.artwork_title ? ` — ${i.artwork_title}` : ""}</p>
                  <p className="font-serif text-xl text-ink mt-1">{i.name} <span className="text-sm text-stone2 font-sans">&lt;{i.email}&gt;</span></p>
                  <p className="text-sm text-stone2 mt-3 whitespace-pre-wrap max-w-2xl">{i.message}</p>
                  <p className="font-mono text-[10px] text-subtle mt-3">{new Date(i.created_at).toLocaleString()}</p>
                </div>
                <select data-testid="admin-inquiry-status-toggle" value={i.status}
                  onChange={(e) => setInquiryStatus(i.id, e.target.value)}
                  className="bg-paper border border-ink/20 rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.15em]">
                  {INQ_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Orders" && (
        <div className="mt-8 space-y-4" data-testid="admin-orders-list">
          {orders.length === 0 && <p className="font-serif text-2xl text-stone2">No orders yet — the first sale will appear here.</p>}
          {orders.map((o) => (
            <div key={o.session_id} className="bg-paper border border-ink/10 deckle p-5 flex flex-wrap justify-between gap-4" data-testid={`admin-order-${o.session_id.slice(-6)}`}>
              <div>
                <p className="font-mono text-xs text-ink">{o.items?.join(" · ")}</p>
                <p className="font-mono text-[10px] text-subtle mt-1">{o.session_id}</p>
                {o.shipping && (
                  <div className="mt-3 font-mono text-xs text-stone2 leading-relaxed" data-testid={`order-shipping-${o.session_id.slice(-6)}`}>
                    <p className="uppercase tracking-[0.2em] text-[9px] text-sage mb-1">Ship to</p>
                    <p>{o.shipping.name}</p>
                    <p>{o.shipping.line1}{o.shipping.line2 ? `, ${o.shipping.line2}` : ""}</p>
                    <p>{[o.shipping.city, o.shipping.state].filter(Boolean).join(", ")} {o.shipping.postal_code} {o.shipping.country}</p>
                    {o.customer_email && <p className="mt-1">{o.customer_email}</p>}
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="font-serif text-xl text-ink">{fmtPrice(o.amount)}</p>
                <p className={`font-mono text-[10px] uppercase tracking-[0.2em] mt-1 ${o.payment_status === "paid" ? "text-sage" : "text-ochre"}`}>{o.payment_status}</p>
                {o.payment_status === "paid" && (
                  <button data-testid={`order-refund-${o.session_id.slice(-6)}`} onClick={() => refundOrder(o.session_id)}
                    className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-terracotta border border-terracotta/40 rounded-md px-3 py-1.5 hover:bg-terracotta hover:text-paper transition-colors">
                    Refund
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Journal" && (
        <div className="mt-8" data-testid="admin-journal-list">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone2" data-testid="journal-count">
              {subscribers.length} {subscribers.length === 1 ? "reader" : "readers"} on the dispatch list
            </p>
            {subscribers.length > 0 && (
              <button
                data-testid="journal-copy-all"
                onClick={() => {
                  navigator.clipboard.writeText(subscribers.map((s) => s.email).join(", "));
                  toast.success("All addresses copied", { description: "Paste them into the Bcc field of your dispatch email." });
                }}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink/20 rounded-full px-5 py-2.5 hover:bg-ink hover:text-linen transition-colors">
                <Copy size={13} /> Copy all addresses
              </button>
            )}
          </div>
          {subscribers.length === 0 && <p className="font-serif text-2xl text-stone2">No readers yet — the first sign-up will appear here.</p>}
          <div className="space-y-3">
            {subscribers.map((s) => (
              <div key={s.id} data-testid={`subscriber-${s.id}`} className="bg-paper border border-ink/10 deckle p-4 flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-serif text-lg text-ink">{s.name}</p>
                  <p className="font-mono text-xs text-stone2 mt-0.5">{s.email}</p>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle self-center">{new Date(s.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[95] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={saveArtwork} onClick={(e) => e.stopPropagation()} data-testid="admin-artwork-form"
            className="bg-linen border border-ink/10 deckle w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-start justify-between">
              <h2 className="font-serif text-3xl text-ink">{editing.id ? "Edit artwork" : "Hang a new work"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" className="w-9 h-9 rounded-full border border-ink/15 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <input required placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={input} data-testid="admin-artwork-form-title" />
              <input placeholder="Series" value={editing.series} onChange={(e) => setEditing({ ...editing, series: e.target.value })} className={input} />
              <input placeholder="Year" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} className={input} />
              <input placeholder="Medium" value={editing.medium} onChange={(e) => setEditing({ ...editing, medium: e.target.value })} className={input} />
              <input placeholder="Dimensions" value={editing.dimensions} onChange={(e) => setEditing({ ...editing, dimensions: e.target.value })} className={input} />
              <input placeholder="Frame" value={editing.frame} onChange={(e) => setEditing({ ...editing, frame: e.target.value })} className={input} />
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={input}>
                {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={input}>
                <option value="painting">Portfolio work</option>
                <option value="framed">Framed original</option>
              </select>
              <input placeholder='Price display, e.g. "$2,400 (Framed)"' value={editing.price_display} onChange={(e) => setEditing({ ...editing, price_display: e.target.value })} className={input} />
              <input type="number" placeholder="Price in cents" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: e.target.value })} className={input} />
              <input required placeholder="Image path or URL" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className={`${input} sm:col-span-2`} data-testid="admin-artwork-form-image" />
              <input placeholder="Palette hexes, comma separated" value={Array.isArray(editing.palette) ? editing.palette.join(", ") : editing.palette} onChange={(e) => setEditing({ ...editing, palette: e.target.value })} className={`${input} sm:col-span-2`} />
              <textarea placeholder="Description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${input} sm:col-span-2`} />
              <label className="flex items-center gap-2 text-sm text-walnut sm:col-span-2">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#B85D38] w-4 h-4" />
                Feature on the home page
              </label>
            </div>
            <button data-testid="admin-artwork-form-save" disabled={saving} type="submit"
              className="mt-6 w-full bg-ink text-linen rounded-full py-3.5 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save artwork"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
